import { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2, BookOpen } from "lucide-react";
import { BasicInformationCard } from "./components/BasicInformationCard";
import { ScheduleDetailsCard } from "./components/ScheduleDetailsCard";
import { StateRequirementsCard } from "./components/StateRequirementsCard";
import { StateRequirements } from "./types";

const eventTypeOptions = [
  { value: "live", label: "Live Event" },
];

const targetAudienceOptions = [
  { value: "teachers", label: "Teachers" },
  { value: "administrators", label: "Administrators" },
  { value: "support_staff", label: "Support Staff" },
  { value: "all", label: "All" },
];

const EditCourse = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newCourse, setNewCourse] = useState<any>(null);
  const courseDataLoaded = useRef(false);

  useEffect(() => {
    if (id === 'new') {
      navigate('/admin/courses/create');
    }
  }, [id, navigate]);

  const fetchCourse = useCallback(async () => {
    if (id === 'new') {
      return null;
    }

    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching course:', error);
      toast({
        title: "Error",
        description: "Failed to load course. Please try again.",
        variant: "destructive",
      });
      throw error;
    }

    console.log("Received from database - start_date:", data.start_date);
    console.log("Received from database - end_date:", data.end_date);

    if (data.start_date) {
      const date = new Date(data.start_date);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      data.start_date = `${year}-${month}-${day}`;
    }
    
    if (data.end_date) {
      const date = new Date(data.end_date);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      data.end_date = `${year}-${month}-${day}`;
    }
    
    if (!data.category) {
      data.category = "Professional Development";
    }

    if (data.state_requirements) {
      const stateReqData = data.state_requirements as unknown;
      const updatedRequirements = Object.assign({}, stateReqData) as StateRequirements;
      
      const mainCategories = ['behavioralIntervention', 'readingPreparation', 'keyWarningSigns', 
                           'suicidePrevention', 'englishLearners', 'americanIndianHistory'];
      
      mainCategories.forEach(category => {
        if (updatedRequirements[category as keyof StateRequirements] && 
            updatedRequirements[`${category}Hours` as keyof StateRequirements] === undefined) {
          (updatedRequirements as any)[`${category}Hours`] = 0;
        }
      });
      
      if (updatedRequirements.culturalCompetency && 
          typeof updatedRequirements.culturalCompetency === 'object' && 
          updatedRequirements.culturalCompetency.enabled) {
        
        // Add the main cultural competency hours field if missing
        if (updatedRequirements.culturalCompetency.enabledHours === undefined) {
          updatedRequirements.culturalCompetency.enabledHours = 0;
        }
      }
      
      data.state_requirements = updatedRequirements as unknown as any;
    }
    
    return data;
  }, [id, navigate, toast]);

  const { data: course, isLoading } = useQuery({
    queryKey: ['course', id],
    queryFn: fetchCourse,
    refetchOnWindowFocus: false,
    enabled: id !== 'new',
  });

  useEffect(() => {
    if (course && !courseDataLoaded.current) {
      courseDataLoaded.current = true;
      setNewCourse({
        ...(course as any),
        presenter_bio: (course as any).presenter_bio || "",
        presenter_image_url: (course as any).presenter_image_url || "",
        event_banner_url: (course as any).event_banner_url || "",
      });
    }
  }, [course]);

  const updateTotalHours = (totalHours: number) => {
    if (newCourse) {
      setNewCourse(prev => ({
        ...prev,
        hours: totalHours
      }));
    }
  };

  const handleUpdate = async () => {
    if (!newCourse) return;
    
    setIsSubmitting(true);
    try {
      const courseToUpdate = {...newCourse};
      
      console.log("Updating with start_date:", courseToUpdate.start_date);
      console.log("Updating with end_date:", courseToUpdate.end_date);
      
      const { error } = await supabase
        .from('courses')
        .update(courseToUpdate)
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Course updated successfully",
      });

      navigate('/admin/courses');
    } catch (error: any) {
      console.error('Error updating course:', error);
      toast({
        title: "Error",
        description: "Failed to update course. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (id === 'new') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!newCourse) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-primary">Course not found</h2>
          <Button 
            onClick={() => navigate('/admin/courses')}
            className="mt-4 font-section text-sm"
          >
            Back to Courses
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-1 px-1 space-y-4">
      <Button 
        variant="outline" 
        onClick={() => navigate(-1)} 
        className="mb-4 font-body border-primary/30 text-primary hover:bg-accent-subtle"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back
      </Button>
      
      <div className="page-header">
        <div className="flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-[#004161]" />
          <h1>Edit Course</h1>
        </div>
        <p>Update course details and settings</p>
      </div>

      <div className="grid gap-4">
        <BasicInformationCard
          newCourse={newCourse}
          setNewCourse={setNewCourse}
          eventTypeOptions={eventTypeOptions}
          targetAudienceOptions={targetAudienceOptions}
        />
        
        <ScheduleDetailsCard
          newCourse={newCourse}
          setNewCourse={setNewCourse}
        />

        <StateRequirementsCard
          newCourse={newCourse}
          setNewCourse={setNewCourse}
          updateTotalHours={updateTotalHours}
        />

        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/courses')}
            className="font-section text-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={isSubmitting}
            className="font-section text-sm"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              'Update Course'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditCourse;
